<template>
  <div class="p-4">
    <div class="p-4 mb-5 border border-solid rounded border-slate-300 bg-slate-50">
      <p class="mb-2 text-xl font-semibold text-slate-900">도메인 설정</p>
      <p class="text-sm leading-6 text-slate-700">사이트별 도메인, HTML 메타 정보, sitemap 정보를 관리하는 페이지입니다.</p>
      <p class="mt-2 text-sm text-slate-700">[ <span class="font-bold text-red-500">*</span> ] 표시는 필수 입력 항목입니다.</p>
      <div class="p-3 mt-3 border border-solid rounded border-amber-300 bg-amber-50">
        <p class="text-sm font-bold text-amber-700 leading-6">
          https://lifeedu.carrotapply.com 도메인을 사용하는 사이트는 별도 설정이 필요하지 않습니다.
        </p>
      </div>
    </div>

    <div v-if="page.isLoading" class="p-3 mb-5 border border-solid rounded border-slate-400 bg-slate-50">
      <p class="text-sm text-slate-700">불러오는 중입니다.</p>
    </div>

    <div class="p-5 mb-5 border-2 border-solid rounded border-slate-600 bg-slate-100">
      <div class="flex items-center justify-between pb-3 mb-4 border-b border-solid border-slate-400">
        <p class="text-lg font-bold">기본 정보</p>
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="p-4 border border-solid rounded border-slate-500 bg-white">
          <p class="mb-2 text-sm font-semibold text-slate-700">idxLeSiteMain</p>
          <p class="text-lg font-bold break-all text-slate-900">{{ page.form.idxLeSiteMain || "-" }}</p>
        </div>
        <div class="p-4 border border-solid rounded border-slate-500 bg-white">
          <p class="mb-2 text-sm font-semibold text-slate-700">Company</p>
          <p class="text-lg font-bold break-all text-slate-900">{{ page.form.companyName || "-" }}</p>
        </div>
      </div>
    </div>

    <div class="p-4 mb-5 border border-solid rounded border-slate-400">
      <p class="mb-4 text-lg font-semibold">Domain</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> 일반 도메인 (@)</span>
          <input
            :value="page.domainBase"
            @input="page.updateDomainBase($event.target.value)"
            type="text"
            class="w-full p-2 border-2 border-solid rounded border-slate-500 focus:outline-none focus:border-blue-600"
            placeholder="연결할 도메인을 입력해주세요. ex) carrotglobal.com"
          />
        </label>
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> www 도메인</span>
          <div
            class="flex items-center overflow-hidden border-2 border-solid rounded border-slate-500 focus-within:border-blue-600"
          >
            <span class="px-3 py-2 text-slate-700 bg-slate-100 border-r border-solid border-slate-400">www.</span>
            <input
              :value="page.domainBase"
              @input="page.updateDomainBase($event.target.value)"
              type="text"
              class="w-full p-2 focus:outline-none"
            />
          </div>
        </label>
      </div>
    </div>

    <div class="p-4 mb-5 border border-solid rounded border-slate-400">
      <p class="mb-4 text-lg font-semibold">HTML</p>
      <div class="grid grid-cols-1 gap-4">
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> html title</span>
          <input
            v-model="page.form.htmlTitle"
            type="text"
            class="w-full p-2 border-2 border-solid rounded border-slate-500 focus:outline-none focus:border-blue-600"
            placeholder="사이트의 탭 이름을 입력해주세요. ex) 평생교육 화상영어"
          />
        </label>
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> html keywords</span>
          <input
            v-model="page.form.htmlKeywords"
            type="text"
            class="w-full p-2 border-2 border-solid rounded border-slate-500 focus:outline-none focus:border-blue-600"
            placeholder="검색 노출 키워드를 쉼표로 구분하여 입력해주세요. ex) 평생교육, 전화화상, 화상영어"
          />
        </label>
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> html description</span>
          <div class="p-3 border-2 border-solid rounded border-slate-500 bg-slate-50">
            <p class="text-sm leading-6 break-keep text-slate-800">{{ page.form.htmlDescription }}</p>
          </div>
        </label>
      </div>
    </div>

    <div class="p-4 mb-5 border border-solid rounded border-slate-400">
      <p class="mb-2 text-lg font-semibold">대표 이미지 설정</p>
      <p class="mb-4 text-sm text-slate-700">링크 공유 및 검색 노출에 사용되는 이미지를 설정합니다.</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="p-4 border border-solid rounded border-slate-300 bg-slate-50">
          <p class="mb-4 font-semibold">
            html property img <span class="text-sm font-normal text-slate-600">(링크 공유 시 미리보기 이미지)</span>
          </p>
          <label class="block mb-3">
            <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> 파일 업로드</span>
            <input
              type="file"
              name="htmlPropertyImg"
              class="mb-2"
              accept="image/*"
              @change="(event) => page.fileChange(event, 'htmlPropertyImg')"
            />
          </label>
          <div v-if="page.form.htmlPropertyImg" class="mb-3">
            <input
              readonly
              type="text"
              :value="page.form.htmlPropertyImg"
              class="w-full p-2 text-blue-600 border border-solid rounded cursor-pointer border-slate-500 bg-white"
              @click="page.openFile(page.form.htmlPropertyImg)"
            />
          </div>
          <div class="block">
            <span class="block mb-2 text-sm font-medium">파일명</span>
            <p class="p-2 border border-solid rounded bg-white border-slate-300 min-h-[42px]">
              {{ page.form.htmlPropertyImgName || "-" }}
            </p>
          </div>
          <button
            v-if="page.form.htmlPropertyImg"
            class="p-2 mt-3 border border-solid rounded text-slate-500 border-slate-300 hover:bg-slate-100"
            @click="page.deleteImage('htmlPropertyImg', 'htmlPropertyImgName')"
          >
            Delete
          </button>
        </div>

        <div class="p-4 border border-solid rounded border-slate-300 bg-slate-50">
          <p class="mb-4 font-semibold">
            html itemprop img <span class="text-sm font-normal text-slate-600">(검색엔진/구조화 데이터용 이미지)</span>
          </p>
          <label class="block mb-3">
            <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> 파일 업로드</span>
            <input
              type="file"
              name="htmlItempropImg"
              class="mb-2"
              accept="image/*"
              @change="(event) => page.fileChange(event, 'htmlItempropImg')"
            />
          </label>
          <div v-if="page.form.htmlItempropImg" class="mb-3">
            <input
              readonly
              type="text"
              :value="page.form.htmlItempropImg"
              class="w-full p-2 text-blue-600 border border-solid rounded cursor-pointer border-slate-500 bg-white"
              @click="page.openFile(page.form.htmlItempropImg)"
            />
          </div>
          <div class="block">
            <span class="block mb-2 text-sm font-medium">파일명</span>
            <p class="p-2 border border-solid rounded bg-white border-slate-300 min-h-[42px]">
              {{ page.form.htmlItempropImgName || "-" }}
            </p>
          </div>
          <button
            v-if="page.form.htmlItempropImg"
            class="p-2 mt-3 border border-solid rounded text-slate-500 border-slate-300 hover:bg-slate-100"
            @click="page.deleteImage('htmlItempropImg', 'htmlItempropImgName')"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div class="p-4 mb-5 border border-solid rounded border-slate-400">
      <p class="mb-4 text-lg font-semibold">Sitemap</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label class="block">
          <span class="block mb-2 text-sm font-medium"><span class="font-bold text-red-500">*</span> sitemap.xml loc url</span>
          <div
            class="flex items-center overflow-hidden border-2 border-solid rounded border-slate-500 focus-within:border-blue-600"
          >
            <span class="px-3 py-2 text-slate-700 bg-slate-100 border-r border-solid border-slate-400">https://</span>
            <input
              :value="page.sitemapBase"
              @input="page.updateSitemapBase($event.target.value)"
              type="text"
              class="w-full p-2 focus:outline-none"
            />
            <span class="px-3 py-2 text-slate-700 bg-slate-100 border-l border-solid border-slate-400">/sitemap.xml</span>
          </div>
        </label>
        <label class="block">
          <span class="block mb-2 text-sm font-medium"
            ><span class="font-bold text-red-500">*</span> www-sitemap.xml loc url</span
          >
          <div
            class="flex items-center overflow-hidden border-2 border-solid rounded border-slate-500 focus-within:border-blue-600"
          >
            <span class="px-3 py-2 text-slate-700 bg-slate-100 border-r border-solid border-slate-400">https://www.</span>
            <input
              :value="page.sitemapBase"
              @input="page.updateSitemapBase($event.target.value)"
              type="text"
              class="w-full p-2 focus:outline-none"
            />
            <span class="px-3 py-2 text-slate-700 bg-slate-100 border-l border-solid border-slate-400">/sitemap.xml</span>
          </div>
        </label>
      </div>
    </div>

    <div
      v-if="page.form.createDate || page.form.updateDate"
      class="p-4 mb-5 border border-solid rounded border-slate-400 bg-slate-50"
    >
      <p class="mb-4 text-lg font-semibold">이력</p>
      <div class="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
        <p><span class="font-semibold">등록일:</span> {{ page.form.createDate || "-" }}</p>
        <p><span class="font-semibold">수정일:</span> {{ page.form.updateDate || "-" }}</p>
      </div>
    </div>

    <div class="flex items-center justify-end gap-4 mb-4">
      <p class="text-sm text-slate-600">저장은 등록/수정을 구분하지 않고 동일하게 처리됩니다.</p>
      <ul class="flex gap-4">
        <li>
          <button class="p-2 text-white bg-red-700 border border-solid rounded cursor-pointer" @click="page.goBack">
            돌아가기
          </button>
        </li>
        <li>
          <button
            :disabled="page.isSaving"
            class="p-2 text-white bg-blue-700 border border-solid rounded cursor-pointer disabled:bg-slate-400 disabled:cursor-not-allowed"
            @click="page.save"
          >
            {{ page.hasExistingSeoData ? "수정" : "저장" }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/plugins/axios.js";

const FIXED_HTML_DESCRIPTION =
  "화상영어 전문 원어민 선생님과 함께! 학습자의 니즈와 레벨에 맞는 체계적인 맞춤 커리큘럼으로 영어 회화 실력 레벨업!";

const DEFAULT_FORM = () => ({
  idxLeSiteMainSeo: "",
  idxLeSiteMain: "",
  companyName: "",
  domain1: "",
  domain2: "",
  htmlTitle: "",
  htmlKeywords: "",
  htmlDescription: "",
  htmlPropertyImg: "",
  htmlPropertyImgName: "",
  htmlItempropImg: "",
  htmlItempropImgName: "",
  locUrl1: "",
  locUrl2: "",
  createDate: "",
  updateDate: "",
});

export default {
  name: "SiteMetaConfig",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const page = reactive({
      form: DEFAULT_FORM(),
      domainBase: "",
      sitemapBase: "",
      isLoading: false,
      isSaving: false,
      hasExistingSeoData: false,

      stripDomainPrefix(value) {
        if (!value) {
          return "";
        }

        return String(value)
          .trim()
          .replace(/^https?:\/\//i, "")
          .replace(/^www\./i, "")
          .replace(/\/+$/g, "");
      },

      setDomainFields(baseValue) {
        const normalizedBase = page.stripDomainPrefix(baseValue);
        page.domainBase = normalizedBase;
        page.form.domain1 = normalizedBase;
        page.form.domain2 = normalizedBase ? `www.${normalizedBase}` : "";
      },

      updateDomainBase(value) {
        page.setDomainFields(value);
        page.setSitemapFields(value);
      },

      stripSitemapValue(value) {
        if (!value) {
          return "";
        }

        return String(value)
          .trim()
          .replace(/^https?:\/\//i, "")
          .replace(/^www\./i, "")
          .replace(/\/sitemap\.xml$/i, "")
          .replace(/\/+$/g, "");
      },

      setSitemapFields(baseValue) {
        const normalizedBase = page.stripSitemapValue(baseValue);
        page.sitemapBase = normalizedBase;
        page.form.locUrl1 = normalizedBase ? `https://${normalizedBase}/sitemap.xml` : "";
        page.form.locUrl2 = normalizedBase ? `https://www.${normalizedBase}/sitemap.xml` : "";
      },

      updateSitemapBase(value) {
        page.setSitemapFields(value);
      },

      normalizeForm(data = {}) {
        const normalizedBase = page.stripDomainPrefix(data.domain1 || data.domain2 || "");
        const normalizedSitemapBase = page.stripSitemapValue(data.locUrl1 || data.locUrl2 || normalizedBase);

        page.form = {
          ...DEFAULT_FORM(),
          idxLeSiteMainSeo: String(data.idxLeSiteMainSeo || ""),
          idxLeSiteMain: String(data.idxLeSiteMain || route.query.idxLeSiteMain || ""),
          companyName: data.companyName || data.kname || data.siteName || route.query.companyName || "",
          domain1: normalizedBase,
          domain2: normalizedBase ? `www.${normalizedBase}` : "",
          htmlTitle: data.htmlTitle || "",
          htmlKeywords: data.htmlKeywords || "",
          htmlDescription: FIXED_HTML_DESCRIPTION,
          htmlPropertyImg: data.htmlPropertyImg || "",
          htmlPropertyImgName: data.htmlPropertyImgName || "",
          htmlItempropImg: data.htmlItempropImg || "",
          htmlItempropImgName: data.htmlItempropImgName || "",
          locUrl1: normalizedSitemapBase ? `https://${normalizedSitemapBase}/sitemap.xml` : "",
          locUrl2: normalizedSitemapBase ? `https://www.${normalizedSitemapBase}/sitemap.xml` : "",
          createDate: data.createDate || "",
          updateDate: data.updateDate || "",
        };

        page.domainBase = normalizedBase;
        page.sitemapBase = normalizedSitemapBase;
      },

      async fileChange(event, fieldName) {
        const inputField = event.target;
        const file = inputField.files && inputField.files[0] ? inputField.files[0] : null;

        if (!file) {
          return;
        }

        try {
          const res = await page.uploadFile(file);
          const uploadUrl = res.data.data.uploadUrl;
          page.form[fieldName] = uploadUrl;
          page.form[`${fieldName}Name`] = page.setImageFileName(uploadUrl);
        } catch (error) {
          console.error("파일 업로드 중 오류가 발생했습니다:", error);
          alert("파일 업로드 중 오류가 발생했습니다.");
          inputField.value = "";
        }
      },

      async uploadFile(file) {
        const formData = new FormData();
        formData.append("file", file);

        return axios.post("/admin/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },

      deleteImage(fieldName, fileNameField) {
        page.form[fieldName] = "";
        page.form[fileNameField] = "";
        const inputField = document.querySelector(`input[name="${fieldName}"]`);
        if (inputField) {
          inputField.value = "";
        }
      },

      setImageFileName(url) {
        if (!url) {
          return "";
        }

        const parts = url.split("/");
        return parts[parts.length - 1];
      },

      openFile(url) {
        if (!url) {
          return;
        }

        window.open(url, "_blank");
      },

      validateForm() {
        const requiredFields = [
          { key: "domain1", label: "일반 도메인 (@)" },
          { key: "domain2", label: "www 도메인" },
          { key: "htmlTitle", label: "html title" },
          { key: "htmlKeywords", label: "html keywords" },
          { key: "htmlDescription", label: "html description" },
          { key: "htmlPropertyImg", label: "html property img" },
          { key: "htmlPropertyImgName", label: "html property img 파일명" },
          { key: "htmlItempropImg", label: "html itemprop img" },
          { key: "htmlItempropImgName", label: "html itemprop img 파일명" },
          { key: "locUrl1", label: "sitemap.xml loc url" },
          { key: "locUrl2", label: "www-sitemap.xml loc url" },
        ];

        const invalidField = requiredFields.find(({ key }) => {
          const value = page.form[key];
          return value === null || value === undefined || String(value).trim() === "";
        });

        if (invalidField) {
          alert(`${invalidField.label} 값을 입력해주세요.`);
          return false;
        }

        return true;
      },

      async load() {
        const idxLeSiteMain = route.query.idxLeSiteMain;
        if (!idxLeSiteMain) {
          alert("잘못된 접근입니다.");
          window.history.back();
          return;
        }

        page.form.idxLeSiteMain = String(idxLeSiteMain);
        page.isLoading = true;

        try {
          const res = await axios.get("/admin/siteMainSeo", {
            params: {
              idxLeSiteMain,
            },
          });

          if (res.data.success && res.data.data && typeof res.data.data === "object") {
            page.hasExistingSeoData = true;
            page.normalizeForm(res.data.data);
          } else {
            page.hasExistingSeoData = false;
            page.normalizeForm({
              idxLeSiteMain,
            });
          }
        } catch (error) {
          console.error("사이트 도메인 설정 조회 중 오류가 발생했습니다:", error);
        } finally {
          page.isLoading = false;
        }
      },

      makePayload() {
        return {
          idxLeSiteMain: Number(page.form.idxLeSiteMain),
          domain1: page.form.domain1,
          domain2: page.form.domain2,
          htmlTitle: page.form.htmlTitle,
          htmlKeywords: page.form.htmlKeywords,
          htmlDescription: FIXED_HTML_DESCRIPTION,
          htmlPropertyImg: page.form.htmlPropertyImg,
          htmlPropertyImgName: page.form.htmlPropertyImgName,
          htmlItempropImg: page.form.htmlItempropImg,
          htmlItempropImgName: page.form.htmlItempropImgName,
          locUrl1: page.form.locUrl1,
          locUrl2: page.form.locUrl2,
        };
      },

      async save() {
        if (!page.form.idxLeSiteMain) {
          alert("idxLeSiteMain 정보가 없습니다.");
          return;
        }

        if (!page.validateForm()) {
          return;
        }

        page.isSaving = true;
        try {
          const res = await axios.post("/admin/siteMainSeo", page.makePayload());

          if (!res.data.success) {
            alert(res.data.message || "저장에 실패했습니다.");
            return;
          }

          if (res.data.data && typeof res.data.data === "object") {
            page.normalizeForm(res.data.data);
          }

          alert(page.hasExistingSeoData ? "도메인 수정이 완료되었습니다." : "도메인 설정이 완료되었습니다.");
          page.hasExistingSeoData = true;
          router.push({ name: "home" });
        } catch (error) {
          console.error("사이트 도메인 설정 저장 중 오류가 발생했습니다:", error);
          alert("저장 중 오류가 발생했습니다.");
        } finally {
          page.isSaving = false;
        }
      },

      goBack() {
        window.history.back();
      },
    });

    onMounted(() => {
      page.load();
    });

    return {
      page,
    };
  },
};
</script>
